import jsPDF from 'jspdf';
import { sv, LW, M, FONT } from './helpers';
import { resolvePdfData } from './pdfEditSchemas';

// ═══════════════════════════════════════════════════════════════════════════════
// LETTER TO INSURANCE COMPANY PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateInsuranceLetterPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'insurance');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const headerAddr = sv(company.exporter_address) +
    (company.email ? ` E MAIL:${sv(company.email)},` : '');
  const addrLines = doc.splitTextToSize(headerAddr, cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4;
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 8;

  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned) ──────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 8;

  // ── To Address ────────────────────────────────────────────────────────
  doc.text('TO,', M, y);
  y += 5;
  const insuranceLines = doc.splitTextToSize(ed.to_block, cw * 0.6);
  doc.text(insuranceLines, M, y);
  y += insuranceLines.length * 4 + 8;

  // ── Salutation ────────────────────────────────────────────────────────
  doc.text('DEAR SIRS,', M, y);
  y += 8;

  // ── Subject (with underline) ──────────────────────────────────────────
  const subjectLine = ed.subject_line;

  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text(subjectLine, M, y);

  // Draw underline
  const textWidth = doc.getTextWidth(subjectLine);
  doc.line(M, y + 1, M + textWidth, y + 1);
  doc.setFontSize(9);
  y += 8;

  // ── Letter Body ───────────────────────────────────────────────────────
  doc.setFontSize(9);
  const introLines = doc.splitTextToSize(ed.intro_text, cw);
  doc.text(introLines, M, y);
  y += introLines.length * 4 + 4;

  // ── Numbered Details ──────────────────────────────────────────────────
  // Fixed positions for alignment
  const labelX = M;
  const colonX = M + 78;
  const valueX = M + 82;
  const valueWidth = cw - 82;

  doc.text('01. CONSIGNEE/ BUYER/ APPLICANT', labelX, y);
  doc.text(':', colonX, y);
  const buyerBlockLines = doc.splitTextToSize(ed.buyer_block, valueWidth);
  doc.text(buyerBlockLines, valueX, y);
  y += buyerBlockLines.length * 4 + 2;

  doc.text('02. EXPORTER/ CONSIGNOR', labelX, y);
  doc.text(':', colonX, y);
  const exporterBlockLines = doc.splitTextToSize(ed.exporter_block, valueWidth);
  doc.text(exporterBlockLines, valueX, y);
  y += exporterBlockLines.length * 4 + 2;

  doc.text('03. INSURANCE REF.', labelX, y);
  doc.text(':', colonX, y);
  const insuranceRefLines = doc.splitTextToSize(ed.insurance_ref, valueWidth);
  doc.text(insuranceRefLines, valueX, y);
  y += insuranceRefLines.length * 4 + 2;

  doc.text('04. INVOICE NO., DATE & VALUE', labelX, y);
  doc.text(':', colonX, y);
  const invoiceValLines = doc.splitTextToSize(ed.invoice_line, valueWidth);
  doc.text(invoiceValLines, valueX, y);
  y += invoiceValLines.length * 4 + 2;

  doc.text('05. COMMODITY & QUANTITY', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.commodity_line, valueX, y);
  y += 6;

  doc.text('06. NET WEIGHT & GROSS WEIGHT', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.weight_line, valueX, y);
  y += 6;

  doc.text('07. DESPATCH DOCUMENT NO. &', labelX, y);
  y += 4;
  doc.text('     DATE', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.despatch_line, valueX, y);
  y += 6;

  doc.text('08. CARRIERS', labelX, y);
  doc.text(':', colonX, y);
  const carrierText = ed.carriers;
  const carrierLines = doc.splitTextToSize(carrierText, valueWidth);
  doc.text(carrierLines, valueX, y);
  y += carrierLines.length * 4 + 2;

  doc.text('09. OTHER REFF.', labelX, y);
  doc.text(':', colonX, y);
  y += 4;
  const otherRef = ed.other_ref;
  const otherRefLines = doc.splitTextToSize(otherRef, cw - 4);
  doc.text(otherRefLines, labelX, y);
  y += otherRefLines.length * 4 + 8;

  // ── Closing ───────────────────────────────────────────────────────────
  doc.text(ed.closing_line, M, y);
  y += 4;
  doc.text('THANKING YOU,', M, y);
  y += 10;

  doc.text('YOURS FAITHFULLY,', M, y);
  y += 4;
  doc.setFont(FONT, 'bold');
  doc.text(`FOR, ${sv(company.exporter_name, '').toUpperCase()}`, M, y);
  y += 15;

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);
  y += 10;

  // ── Copy Forwarded To ─────────────────────────────────────────────────
  doc.setFont(FONT, 'bold');
  const cfText = 'COPY FORWARDED TO :';
  doc.text(cfText, M, y);
  const cfWidth = doc.getTextWidth(cfText);
  doc.line(M, y + 1, M + cfWidth, y + 1);
  y += 5;
  doc.setFont(FONT, 'normal');

  // Buyer details
  const buyerCopy = ed.copy_buyer;
  const buyerCopyLines = doc.splitTextToSize(buyerCopy, cw - 8);
  doc.text('•', M, y);
  doc.text(buyerCopyLines, M + 4, y);
  y += buyerCopyLines.length * 4;

  // Bank details
  const bankCopy = ed.copy_bank;
  const bankCopyLines = doc.splitTextToSize(bankCopy, cw - 8);
  doc.text('•', M, y);
  doc.text(bankCopyLines, M + 4, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`InsuranceLetter_${sv(document.invoice_no, document.id)}.pdf`);
}
